// Interactive BPM readout. The digits *are* the knob.
//
// Idle state reads live BPM from cpsRef via the shared ticker — same
// mechanism as the neighboring cycle readout, so the numbers stay in
// lock-step with the engine with zero React reconciliation.
//
// On mousedown the component arms a drag session: mousemove deltas
// translate to BPM deltas, and the digit display switches to a local
// scrub value until release. On mouseup we commit the final BPM
// upward via onCommit — the parent rewrites the song's setcpm
// statement, which flows back through the editor → parser → hot-swap
// pipeline.
//
// Shift-drag is fine mode (0.1 BPM per px instead of 0.5). Arrow keys
// bump ±1 with Shift doing ±10. Double-click resets to 120.

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { subscribeTick } from '../strudel';

const COARSE_BPM_PER_PX = 0.5;
const FINE_BPM_PER_PX = 0.1;
const KEY_STEP_COARSE = 1;
const KEY_STEP_SHIFT = 10;
const DEFAULT_BPM = 120;
const MIN_BPM = 20;
const MAX_BPM = 400;

type Props = {
  cpsRef: MutableRefObject<number>;
  active: boolean;
  onCommit: (bpm: number) => void;
};

export const TempoScrubber = memo(TempoScrubberInner);

function TempoScrubberInner({ cpsRef, active, onCommit }: Props) {
  // Dragging state — `null` when idle, a BPM value while the user is
  // scrubbing. The render chooses which to display (`scrubBpm ??
  // live`) so the digits stay pinned to user intent during a drag,
  // then snap back to engine-reported BPM on release.
  const [scrubBpm, setScrubBpm] = useState<number | null>(null);
  const scrubbing = scrubBpm !== null;

  const digitRef = useRef<HTMLSpanElement>(null);
  const barRef = useRef<HTMLSpanElement>(null);
  const lastTextRef = useRef<string>('');
  const scrubRef = useRef<number | null>(null);
  scrubRef.current = scrubBpm;

  // Idle-state textContent update — driven by the shared ticker,
  // reads cpsRef and writes the digit span only when the value
  // changes. Skipped while scrubbing so local drag state wins.
  useEffect(() => {
    return subscribeTick(() => {
      if (scrubRef.current !== null) return;
      const cps = cpsRef.current;
      const next = cps > 0 ? String(Math.round(cps * 60 * 4)) : '---';
      if (next !== lastTextRef.current) {
        lastTextRef.current = next;
        const span = digitRef.current;
        if (span) span.textContent = next;
        const bar = barRef.current;
        if (bar) bar.style.width = barPercent(cps > 0 ? Math.round(cps * 60 * 4) : 0) + '%';
      }
    });
  }, [cpsRef]);

  // Scrub-state display: while dragging, update digits + bar directly
  // on every React render. The render is cheap enough (setScrubBpm
  // is the only state), and writing through the ref keeps animation
  // consistent with the idle code path.
  useEffect(() => {
    if (scrubBpm === null) return;
    const text = String(scrubBpm);
    lastTextRef.current = text;
    const span = digitRef.current;
    if (span) span.textContent = text;
    const bar = barRef.current;
    if (bar) bar.style.width = barPercent(scrubBpm) + '%';
  }, [scrubBpm]);

  const commitScrub = useCallback(
    (bpm: number) => {
      const clamped = clamp(Math.round(bpm));
      onCommit(clamped);
    },
    [onCommit],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      const startY = e.clientY;
      const startBpm = bpmFromCps(cpsRef.current) ?? DEFAULT_BPM;
      setScrubBpm(startBpm);

      const onMove = (ev: MouseEvent) => {
        const dy = startY - ev.clientY; // up = positive = higher BPM
        const perPx = ev.shiftKey ? FINE_BPM_PER_PX : COARSE_BPM_PER_PX;
        const raw = startBpm + dy * perPx;
        // Visually display an integer during drag — fractional BPMs
        // are committed rounded anyway, and a jittering decimal
        // doesn't read as a confident knob.
        setScrubBpm(clamp(Math.round(raw)));
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        const final = scrubRef.current;
        setScrubBpm(null);
        if (final !== null && final !== startBpm) commitScrub(final);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [cpsRef, commitScrub],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const step = e.shiftKey ? KEY_STEP_SHIFT : KEY_STEP_COARSE;
        const dir = e.key === 'ArrowUp' ? 1 : -1;
        const current = bpmFromCps(cpsRef.current) ?? DEFAULT_BPM;
        commitScrub(current + dir * step);
      } else if (e.key === 'Enter' || e.key === ' ') {
        // No-op; the button form-submit default is fine but we don't
        // want space to scroll the page.
        e.preventDefault();
      }
    },
    [cpsRef, commitScrub],
  );

  const handleDoubleClick = useCallback(() => {
    commitScrub(DEFAULT_BPM);
  }, [commitScrub]);

  // Initial bar width on first render — ticker takes over after mount.
  const initialBpm = bpmFromCps(cpsRef.current) ?? 0;

  return (
    <div className="flex flex-col items-end">
      <span className="text-[8px] tracking-[0.3em] uppercase text-zinc-500 mb-0.5 leading-none">
        bpm
      </span>
      <span
        role="slider"
        tabIndex={0}
        aria-label="Tempo in BPM"
        aria-valuemin={MIN_BPM}
        aria-valuemax={MAX_BPM}
        aria-valuenow={scrubBpm ?? Math.round(initialBpm) ?? 0}
        aria-valuetext={String(scrubBpm ?? Math.round(initialBpm) ?? 0) + ' BPM'}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        onDoubleClick={handleDoubleClick}
        className={
          'group relative font-display text-[22px] leading-none tabular-nums ' +
          'transition-colors select-none outline-none ' +
          'cursor-ns-resize focus-visible:text-yellow-200 ' +
          (scrubbing
            ? 'text-emerald-300'
            : active
              ? 'text-yellow-300'
              : 'text-zinc-600')
        }
        style={
          scrubbing
            ? { textShadow: '0 0 14px rgba(134, 239, 172, 0.55)' }
            : active
              ? { textShadow: '0 0 12px rgba(253, 224, 71, 0.35)' }
              : undefined
        }
        title="Drag vertically to scrub · Shift for fine · ↑↓ to nudge · double-click to reset"
      >
        <span ref={digitRef}>{initialBpm > 0 ? String(Math.round(initialBpm)) : '---'}</span>

        {/* Scrub indicator — thin bar under the digits, visible only
            while scrubbing. Colored emerald to match the "signal
            captured" semantic elsewhere in the console. */}
        <span
          aria-hidden="true"
          className={
            'absolute left-0 right-0 -bottom-1.5 h-px pointer-events-none ' +
            (scrubbing ? 'opacity-100' : 'opacity-0') +
            ' transition-opacity duration-75'
          }
        >
          <span className="absolute inset-0 bg-zinc-800" />
          <span
            ref={barRef}
            className="absolute inset-y-0 left-0 bg-emerald-300"
            style={{
              width: barPercent(initialBpm || DEFAULT_BPM) + '%',
              boxShadow: '0 0 6px rgba(134, 239, 172, 0.55)',
            }}
          />
        </span>
      </span>
    </div>
  );
}

// ─── Pure helpers ───────────────────────────────────────────────────

function bpmFromCps(cps: number): number | null {
  if (!Number.isFinite(cps) || cps <= 0) return null;
  return cps * 60 * 4;
}

function clamp(bpm: number): number {
  if (!Number.isFinite(bpm)) return DEFAULT_BPM;
  return Math.max(MIN_BPM, Math.min(MAX_BPM, bpm));
}

function barPercent(bpm: number): number {
  if (bpm <= 0) return 0;
  const pct = ((bpm - MIN_BPM) / (MAX_BPM - MIN_BPM)) * 100;
  return Math.max(0, Math.min(100, pct));
}
